import React, { useContext } from 'react';
import { GroupIntakeContext } from './GroupIntakeContext';
import GroupSummaryCard from './GroupSummaryCard';
import FacilityCard from './FacilityCard';
import ChecklistSidebar from './ChecklistSidebar';
import { Container, Row, Col } from 'react-bootstrap';
import './ReviewStep.css'; // For premium glass/gradient styles
import { useListing } from './contexts/ListingContext';


/**
 * Summary/Highlights component for reviewing group intake information
 * Reimagined as a more visually appealing dashboard-like view
 */
const ReviewStep = () => {
  const context = useContext(GroupIntakeContext);
  const { group, facilities } = context;
  const { getListingsForFacility } = useListing();

  // Validation checklist logic
  const checklist = [
    { label: 'Group Name', required: true, complete: !!group?.name },
    { label: 'Group Contact', required: true, complete: !!group?.contact?.email },
    { label: 'At least 1 Facility', required: true, complete: facilities && facilities.length > 0 },
    ...((facilities || []).map((f, i) => ({
      label: `Facility: ${f.facilityName || f.name || 'Untitled'}`,
      required: true,
      complete: !!f.facilityName || !!f.name
    }))),
    ...((facilities || []).flatMap((f, i) => {
      const facilityId = f._id || f.id;
      const listings = getListingsForFacility ? getListingsForFacility(facilityId) : [];
      if (!listings || listings.length === 0) {
        return [{
          label: `No Listings (Facility ${f.facilityName || f.name || i + 1})`,
          required: true,
          complete: false
        }];
      }
      return listings.map((l, j) => {
        const isPublished = l.reviewStatus === 'reviewed';
        return {
          label: `Listing: ${l.title || 'Untitled'} (Facility ${f.facilityName || f.name || i + 1}) ` +
            (isPublished
              ? '<span class="badge bg-success ms-2">Published</span>'
              : '<span class="badge bg-secondary ms-2">Draft</span>'),
          required: true,
          complete: !!l.title && !!l.compensation && (!!l.sourceListingId || !!l._id) && isPublished,
          isPublished,
        };
      });
    }))
  ];

  if (!context || !group) {
    return (
      <div className="review-error">
        <div className="alert alert-danger">Unable to load group data for review.</div>
      </div>
    );
  }

  // Edit navigation helpers
  const handleEditGroup = () => context.setCurrentStep?.(0);
  const handleEditFacility = idx => context.setCurrentStep?.(1) && context.setPreviewFacilityIndex?.(idx);
  const handleEditListing = (facIdx, listIdx) => {
    context.setCurrentStep?.(2);
    context.setPreviewFacilityIndex?.(facIdx);
    context.setPreviewListingIndex?.(listIdx);
  };

  return (
    <Container fluid className="review-step-container animated-gradient-bg">
      <Row>
        <Col md={12} className="mb-3">
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div className="mb-3">
              <div className="alert alert-info d-flex align-items-center" style={{ fontSize: '1.08rem', fontWeight: 500 }}>
                <span className="me-2" role="img" aria-label="info">ℹ️</span>
                <span>
                  <strong>Important:</strong> Each listing must be <span className="text-success">Published</span> individually before it will appear to Providers. Use the <strong>Publish</strong> button on each listing card below. You can also <strong>Unpublish</strong> a listing at any time.
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={8} className="mb-4">
          <GroupSummaryCard group={group} onEdit={handleEditGroup} />
          {(facilities || []).map((facility, fIdx) => (
            <FacilityCard
              key={facility._id || fIdx}
              facility={facility}
              facilityIndex={fIdx}
              onEdit={() => handleEditFacility(fIdx)}
              onEditListing={lIdx => handleEditListing(fIdx, lIdx)}
            />
          ))}
        </Col>
        <Col md={4}>
          <ChecklistSidebar checklist={checklist} />
        </Col>
      </Row>
      {/* Call-to-Action: Show only if checklist is complete and there is at least one published listing */}
      {(() => {
        // Determine checklist completion and published listings
        const checklistComplete = checklist.every(item => item.complete);
        const publishedListings = (facilities || []).flatMap(facility => {
          const facilityId = facility._id || facility.id;
          const listings = getListingsForFacility ? getListingsForFacility(facilityId) : [];
          return listings.filter(l => l.reviewStatus === 'reviewed');
        });
        if (checklistComplete && publishedListings.length > 0) {
          return (
            <div className="d-flex justify-content-center mt-4 mb-3">
              <button
                className="find-providers-cta premium-shadow animate-glow"
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  borderRadius: 12,
                  padding: '0.85rem 2.5rem',
                  background: 'linear-gradient(90deg, #38d996 0%, #0d6efd 100%)',
                  color: '#fff',
                  border: 'none',
                  boxShadow: '0 4px 24px 0 #38d99644',
                  letterSpacing: 1,
                  transition: 'transform 0.12s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.85rem',
                  cursor: 'pointer',
                  outline: 'none',
                  filter: 'drop-shadow(0 1.5px 8px #0d6efd33) blur(0.5px)',
                  animation: 'pulseGlow 2s infinite',
                }}
                onClick={() => {
                  // TODO: Route to provider swipe/match page
                  window.location.href = '/group-provider-swipe';
                }}
                title="Start finding providers for your published listings!"
              >
                <svg width="27" height="27" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 19c-4.418 0-8-3.582-8-8 0-4.418 3.582-8 8-8s8 3.582 8 8c0 4.418-3.582 8-8 8zm0 0v4m-4-4h8"/></svg>
                <span>Find Providers</span>
              </button>
            </div>
          );
        }
        return null;
      })()}
    </Container>
  );
};

export default ReviewStep;
